/**
 * Custom hook for Asset Management
 */

import { useState, useCallback, useEffect } from "react";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { useTenantContext } from "./use-tenant-context";
import {
  LIST_ASSETS,
  GET_ASSET,
  CREATE_ASSET,
  UPDATE_ASSET,
  DELETE_ASSET,
  ARCHIVE_ASSET,
  CREATE_BULK_ASSETS,
  EXPORT_ASSETS,
  GENERATE_ASSET_UPLOAD_URL,
  ASSOCIATE_FILE_WITH_ASSET,
  REMOVE_FILE_FROM_ASSET,
} from "../services/graphql/asset-operations";
import { GENERATE_ASSET_LINK } from "../services/graphql/asset-link-queries";
import {
  Asset,
  AssetFilter,
  CreateAssetInput,
  UpdateAssetInput,
  BulkCreateAssetInput,
  ExportFormat,
  AssetExportResult,
  UploadUrl,
} from "../types/asset-types";

export interface UseAssetsOptions {
  filter?: AssetFilter;
  limit?: number;
  autoFetch?: boolean;
}

export interface UseAssetsResult {
  // Data
  assets: Asset[];
  loading: boolean;
  error: string | undefined;
  total: number;
  hasMore: boolean;

  // Actions
  refetch: () => void;
  loadMore: () => void;
  createAsset: (input: CreateAssetInput) => Promise<Asset>;
  createBulkAssets: (input: BulkCreateAssetInput) => Promise<Asset[]>;
  updateAsset: (id: string, input: UpdateAssetInput) => Promise<Asset>;
  deleteAsset: (id: string) => Promise<boolean>;
  archiveAsset: (id: string) => Promise<Asset>;
  exportAssets: (
    filter?: AssetFilter,
    format?: ExportFormat
  ) => Promise<AssetExportResult>;
  generateUploadUrl: (
    assetId: string,
    filename: string,
    contentType: string
  ) => Promise<UploadUrl>;
  associateFile: (assetId: string, fileId: string) => Promise<Asset>;
  removeFile: (assetId: string, fileId: string) => Promise<Asset>;

  // Filter management
  filter: AssetFilter;
  setFilter: (filter: AssetFilter) => void;
  clearFilter: () => void;
}

export function useAssets(options: UseAssetsOptions = {}): UseAssetsResult {
  const { filter: initialFilter = {}, limit = 20, autoFetch = true } = options;
  const [filter, setFilter] = useState<AssetFilter>(initialFilter);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [total, setTotal] = useState(0);
  const [nextToken, setNextToken] = useState<string | undefined>();

  // Wait for tenant context to be ready before making queries
  const { isReady: isTenantContextReady, isLoading: isTenantContextLoading } =
    useTenantContext();

  console.log("🏠 useAssets - Tenant context:", {
    isTenantContextReady,
    isTenantContextLoading,
    autoFetch,
    shouldSkip: !autoFetch || !isTenantContextReady,
  });

  // Query for listing assets - only run when tenant context is ready
  const {
    loading: listLoading,
    error: listError,
    refetch: refetchList,
    fetchMore,
  } = useQuery(LIST_ASSETS, {
    variables: { filter, limit },
    skip: !autoFetch || !isTenantContextReady, // Skip until tenant context is ready
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.listAssets) {
        setAssets(data.listAssets.items || []);
        setTotal(data.listAssets.total || 0);
        setNextToken(data.listAssets.nextToken);
      }
    },
    onError: (error) => {
      console.error("Error fetching assets:", error);
    },
  });

  // Effect to trigger query when tenant context becomes ready
  useEffect(() => {
    if (isTenantContextReady && autoFetch) {
      console.log("🔄 Tenant context ready, triggering asset query");
      refetchList();
    }
  }, [isTenantContextReady, autoFetch, refetchList]);

  // Mutations
  const [createAssetMutation] = useMutation(CREATE_ASSET, {
    errorPolicy: "all",
    // Remove onCompleted to prevent duplicate additions to state
    // The asset will be added to state manually in the createAsset function
  });

  const [createBulkAssetsMutation] = useMutation(CREATE_BULK_ASSETS, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.createBulkAssets) {
        setAssets((prev) => [...data.createBulkAssets, ...prev]);
        setTotal((prev) => prev + data.createBulkAssets.length);
      }
    },
  });

  const [updateAssetMutation] = useMutation(UPDATE_ASSET, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.updateAsset) {
        setAssets((prev) =>
          prev.map((asset) =>
            asset.id === data.updateAsset.id ? data.updateAsset : asset
          )
        );
      }
    },
  });

  const [deleteAssetMutation] = useMutation(DELETE_ASSET, {
    errorPolicy: "all",
    update: (cache, result, { variables }) => {
      if (result.data?.deleteAsset && variables?.id) {
        // Remove from Apollo cache
        cache.evict({
          id: cache.identify({
            __typename: "Asset",
            id: variables.id,
          }),
        });
        cache.gc();

        // Also invalidate any list queries
        cache.evict({ fieldName: "listAssets" });
      }
    },
  });

  const [archiveAssetMutation] = useMutation(ARCHIVE_ASSET, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.archiveAsset) {
        setAssets((prev) =>
          prev.map((asset) =>
            asset.id === data.archiveAsset.id ? data.archiveAsset : asset
          )
        );
      }
    },
  });

  const client = useApolloClient();

  const [generateUploadUrlMutation] = useMutation(GENERATE_ASSET_UPLOAD_URL, {
    errorPolicy: "all",
  });

  const [associateFileMutation] = useMutation(ASSOCIATE_FILE_WITH_ASSET, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.associateFileWithAsset) {
        setAssets((prev) =>
          prev.map((asset) =>
            asset.id === data.associateFileWithAsset.id
              ? data.associateFileWithAsset
              : asset
          )
        );
      }
    },
  });

  const [removeFileMutation] = useMutation(REMOVE_FILE_FROM_ASSET, {
    errorPolicy: "all",
    onCompleted: (data) => {
      if (data?.removeFileFromAsset) {
        setAssets((prev) =>
          prev.map((asset) =>
            asset.id === data.removeFileFromAsset.id
              ? data.removeFileFromAsset
              : asset
          )
        );
      }
    },
  });

  const [generateAssetLinkMutation] = useMutation(GENERATE_ASSET_LINK, {
    errorPolicy: "all",
  });

  // Actions
  const refetch = useCallback(() => {
    refetchList();
  }, [refetchList]);

  const loadMore = useCallback(async () => {
    if (!nextToken || listLoading) return;

    try {
      const result = await fetchMore({
        variables: {
          filter,
          limit,
          nextToken,
        },
      });

      if (result.data?.listAssets) {
        const newAssets = result.data.listAssets.items || [];
        setAssets((prev) => [...prev, ...newAssets]);
        setNextToken(result.data.listAssets.nextToken);
      }
    } catch (error) {
      console.error("Error loading more assets:", error);
    }
  }, [nextToken, listLoading, fetchMore, filter, limit]);

  // Track if asset creation is in progress to prevent duplicates
  const [isCreatingAsset, setIsCreatingAsset] = useState(false);

  const createAsset = useCallback(
    async (input: CreateAssetInput): Promise<Asset> => {
      // Prevent duplicate asset creation
      if (isCreatingAsset) {
        console.warn(
          "🚫 Asset creation already in progress - blocking duplicate"
        );
        throw new Error("Asset creation already in progress");
      }

      const debugId = `${Date.now()}-${Math.random()
        .toString(36)
        .substr(2, 5)}`;
      console.log(`🎯 [${debugId}] FRONTEND ASSET CREATION STARTED`);
      console.log(`🎯 [${debugId}] Input:`, JSON.stringify(input, null, 2));

      setIsCreatingAsset(true);

      try {
        console.log(`🎯 [${debugId}] Calling GraphQL createAsset mutation`);
        const result = await createAssetMutation({
          variables: { input },
        });

        if (result.errors) {
          console.error(`🎯 [${debugId}] GraphQL errors:`, result.errors);
          throw new Error(
            result.errors[0]?.message || "Failed to create asset"
          );
        }

        if (!result.data?.createAsset) {
          console.error(`🎯 [${debugId}] No asset data returned`);
          throw new Error("No asset data returned");
        }

        const createdAsset = result.data.createAsset;
        console.log(
          `🎯 [${debugId}] Asset created successfully:`,
          createdAsset.id
        );

        // Add the asset to local state
        setAssets((prev) => [createdAsset, ...prev]);
        setTotal((prev) => prev + 1);

        // Automatically generate an asset link for the new asset
        try {
          console.log(
            `🎯 [${debugId}] Generating asset link for asset:`,
            createdAsset.id
          );
          await generateAssetLinkMutation({
            variables: {
              input: {
                assetId: createdAsset.id,
                metadata: {
                  description: `Trackable link for ${createdAsset.name}`,
                },
                generateQRCode: false, // Don't auto-generate QR code, let user choose
              },
            },
          });
          console.log(`🎯 [${debugId}] Asset link generated successfully`);
        } catch (linkError) {
          console.warn(
            `🎯 [${debugId}] Failed to auto-generate asset link:`,
            linkError
          );
          // Don't fail asset creation if link generation fails
        }

        console.log(
          `🎯 [${debugId}] FRONTEND ASSET CREATION COMPLETED SUCCESSFULLY`
        );
        return createdAsset;
      } catch (error) {
        console.error(`🎯 [${debugId}] FRONTEND ASSET CREATION FAILED:`, error);
        throw error;
      } finally {
        console.log(`🎯 [${debugId}] Setting isCreatingAsset to false`);
        setIsCreatingAsset(false);
      }
    },
    [createAssetMutation, generateAssetLinkMutation, isCreatingAsset]
  );

  const createBulkAssets = useCallback(
    async (input: BulkCreateAssetInput): Promise<Asset[]> => {
      try {
        const result = await createBulkAssetsMutation({
          variables: { input },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to create bulk assets"
          );
        }

        if (!result.data?.createBulkAssets) {
          throw new Error("No asset data returned");
        }

        return result.data.createBulkAssets;
      } catch (error) {
        console.error("Error creating bulk assets:", error);
        throw error;
      }
    },
    [createBulkAssetsMutation]
  );

  const updateAsset = useCallback(
    async (id: string, input: UpdateAssetInput): Promise<Asset> => {
      try {
        const result = await updateAssetMutation({
          variables: { id, input },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to update asset"
          );
        }

        if (!result.data?.updateAsset) {
          throw new Error("No asset data returned");
        }

        return result.data.updateAsset;
      } catch (error) {
        console.error("Error updating asset:", error);
        throw error;
      }
    },
    [updateAssetMutation]
  );

  const deleteAsset = useCallback(
    async (id: string): Promise<boolean> => {
      try {
        const result = await deleteAssetMutation({
          variables: { id },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to delete asset"
          );
        }

        const success = result.data?.deleteAsset || false;
        if (success) {
          setAssets((prev) => prev.filter((asset) => asset.id !== id));
          setTotal((prev) => prev - 1);

          // Force a refetch to ensure UI is in sync
          setTimeout(() => {
            refetch();
          }, 100);
        }

        return success;
      } catch (error) {
        console.error("Error deleting asset:", error);
        throw error;
      }
    },
    [deleteAssetMutation]
  );

  const exportAssets = useCallback(
    async (
      exportFilter?: AssetFilter,
      format: ExportFormat = ExportFormat.CSV
    ): Promise<AssetExportResult> => {
      try {
        const result = await client.query({
          query: EXPORT_ASSETS,
          variables: {
            filter: exportFilter || filter,
            format,
          },
          fetchPolicy: "no-cache", // Always fetch fresh export data
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to export assets"
          );
        }

        if (!result.data?.exportAssets) {
          throw new Error("No export data returned");
        }

        return result.data.exportAssets;
      } catch (error) {
        console.error("Error exporting assets:", error);
        throw error;
      }
    },
    [client, filter]
  );

  const generateUploadUrl = useCallback(
    async (
      assetId: string,
      filename: string,
      contentType: string
    ): Promise<UploadUrl> => {
      try {
        const result = await generateUploadUrlMutation({
          variables: { assetId, filename, contentType },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to generate upload URL"
          );
        }

        if (!result.data?.generateAssetUploadUrl) {
          throw new Error("No upload URL returned");
        }

        return result.data.generateAssetUploadUrl;
      } catch (error) {
        console.error("Error generating upload URL:", error);
        throw error;
      }
    },
    [generateUploadUrlMutation]
  );

  const associateFile = useCallback(
    async (assetId: string, fileId: string): Promise<Asset> => {
      try {
        const result = await associateFileMutation({
          variables: { assetId, fileId },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to associate file"
          );
        }

        if (!result.data?.associateFileWithAsset) {
          throw new Error("No asset data returned");
        }

        return result.data.associateFileWithAsset;
      } catch (error) {
        console.error("Error associating file:", error);
        throw error;
      }
    },
    [associateFileMutation]
  );

  const removeFile = useCallback(
    async (assetId: string, fileId: string): Promise<Asset> => {
      try {
        const result = await removeFileMutation({
          variables: { assetId, fileId },
        });

        if (result.errors) {
          throw new Error(result.errors[0]?.message || "Failed to remove file");
        }

        if (!result.data?.removeFileFromAsset) {
          throw new Error("No asset data returned");
        }

        return result.data.removeFileFromAsset;
      } catch (error) {
        console.error("Error removing file:", error);
        throw error;
      }
    },
    [removeFileMutation]
  );

  const clearFilter = useCallback(() => {
    setFilter({});
  }, []);

  // Update filter effect
  useEffect(() => {
    if (autoFetch) {
      refetch();
    }
  }, [filter, autoFetch, refetch]);

  // Archive asset
  const archiveAsset = useCallback(
    async (id: string): Promise<Asset> => {
      try {
        const result = await archiveAssetMutation({
          variables: { id },
        });

        if (result.errors) {
          throw new Error(
            result.errors[0]?.message || "Failed to archive asset"
          );
        }

        if (!result.data?.archiveAsset) {
          throw new Error("No asset data returned");
        }

        return result.data.archiveAsset;
      } catch (error) {
        console.error("Error archiving asset:", error);
        throw error;
      }
    },
    [archiveAssetMutation]
  );

  return {
    // Data
    assets,
    loading: listLoading || !isTenantContextReady, // Include tenant context loading
    error: listError?.message,
    total,
    hasMore: !!nextToken,

    // Actions
    refetch,
    loadMore,
    createAsset,
    createBulkAssets,
    updateAsset,
    deleteAsset,
    archiveAsset,
    exportAssets,
    generateUploadUrl,
    associateFile,
    removeFile,

    // Filter management
    filter,
    setFilter,
    clearFilter,
  };
}

// Hook for single asset
export function useAsset(id: string) {
  const { isReady: isTenantContextReady } = useTenantContext();

  const { data, loading, error, refetch } = useQuery(GET_ASSET, {
    variables: { id },
    skip: !id || !isTenantContextReady, // Skip until tenant context is ready
    errorPolicy: "all",
  });

  return {
    asset: data?.getAsset,
    loading: loading || !isTenantContextReady,
    error: error?.message,
    refetch,
  };
}
